import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Calendar, MapPin, Video, Users as UsersIcon, Check, Clock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import BabasahebPortrait from "../components/BabasahebPortrait";
import EventCover, { EventTypeIcon } from "../components/EventCover";
import AddToCalendarButton from "../components/AddToCalendarButton";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function modeBadge(mode) {
  if (mode === "virtual") return { label: "Zoom", icon: Video, color: "bg-saffron/15 text-saffron" };
  if (mode === "hybrid") return { label: "Hybrid", icon: Video, color: "bg-gold/20 text-gold-dark" };
  return { label: "In Person", icon: MapPin, color: "bg-navy/10 text-navy" };
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myRsvps, setMyRsvps] = useState(new Set());
  const [activeMonth, setActiveMonth] = useState(null); // null = all
  const [filter, setFilter] = useState("all"); // all | upcoming | past
  const { user } = useAuth();

  const load = async () => {
    try {
      const { data } = await api.get("/events?upcoming_only=false");
      setEvents(data);
      if (user) {
        try {
          const { data: mine } = await api.get("/events/my-rsvps");
          setMyRsvps(new Set((mine.events || []).map((e) => e.event_id)));
        } catch { /* ignore */ }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [user]); // eslint-disable-line

  const rsvp = async (eventId) => {
    if (!user) { toast.error("Please log in to RSVP"); return; }
    try {
      await api.post("/events/rsvp", { event_id: eventId });
      setMyRsvps(new Set([...myRsvps, eventId]));
      toast.success("RSVP confirmed. See you there!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const monthsWithEvents = useMemo(() => {
    const set = new Set(events.map((e) => new Date(e.event_date).getMonth()));
    return set;
  }, [events]);

  const now = Date.now();
  const filtered = useMemo(() => {
    let out = [...events];
    if (activeMonth != null) out = out.filter((e) => new Date(e.event_date).getMonth() === activeMonth);
    if (filter === "upcoming") out = out.filter((e) => new Date(e.event_date).getTime() >= now);
    if (filter === "past") out = out.filter((e) => new Date(e.event_date).getTime() < now);
    out.sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
    return out;
  }, [events, activeMonth, filter, now]);

  return (
    <>
      {/* HERO with Babasaheb */}
      <section className="relative bg-navy text-pearl overflow-hidden" data-testid="events-hero">
        <div className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: "radial-gradient(circle at 80% 50%, rgba(207,160,82,0.4), transparent 50%)",
          }}
        />
        <div className="relative section-container py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow text-gold">Yearly Programme · 2026</p>
            <h1 className="mt-3 font-display text-5xl sm:text-6xl leading-tight">Come sit with us.</h1>
            <p className="mt-6 text-pearl/80 max-w-2xl text-lg leading-relaxed">
              Our annual cycle of nine sacred gatherings, from <span className="text-gold">Bhima Koregaon Shaurya Din</span> in
              January to our <span className="text-gold">New Year Celebration</span> in December. Held across Northern UK
              and online, open to members and friends in the spirit of Dhamma.
            </p>
            <p className="mt-6 italic text-gold font-display text-2xl">
              "Educate, Agitate, Organise."
              <span className="block not-italic mt-1 text-sm uppercase tracking-[0.25em] text-pearl/60">— Dr. B.R. Ambedkar</span>
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <BabasahebPortrait className="rounded-sm shadow-2xl shadow-black/30" aspect="aspect-[4/5]" />
              <span className="absolute -bottom-3 left-3 right-3 bg-gold text-navy text-[11px] uppercase tracking-[0.22em] px-4 py-2 text-center">
                Babasaheb · 14 Apr 1891 — 6 Dec 1956
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* INTERACTIVE TIMELINE */}
      <section className="py-12 bg-pearl border-b border-navy/10 sticky top-20 z-30 backdrop-blur-md bg-pearl/85" data-testid="events-timeline">
        <div className="section-container">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <p className="eyebrow">Browse the year</p>
              <h2 className="mt-1 font-display text-2xl sm:text-3xl text-navy">Tap a month or filter to explore</h2>
            </div>
            <div className="flex gap-2 text-xs uppercase tracking-widest" role="tablist" data-testid="events-filters">
              {[
                { k: "all", l: "All" },
                { k: "upcoming", l: "Upcoming" },
                { k: "past", l: "Past" },
              ].map((f) => (
                <button
                  key={f.k}
                  onClick={() => setFilter(f.k)}
                  data-testid={`filter-${f.k}`}
                  className={`px-3.5 py-2 rounded-sm border transition-colors ${
                    filter === f.k
                      ? "bg-navy text-pearl border-navy"
                      : "bg-white text-navy/70 border-navy/15 hover:border-navy/40"
                  }`}
                >
                  {f.l}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-6 sm:grid-cols-12 gap-2">
            {MONTHS.map((m, idx) => {
              const has = monthsWithEvents.has(idx);
              const active = activeMonth === idx;
              return (
                <button
                  key={m}
                  disabled={!has}
                  onClick={() => setActiveMonth(active ? null : idx)}
                  data-testid={`month-${m.toLowerCase()}`}
                  className={`relative flex flex-col items-center justify-center py-4 rounded-sm border transition-all ${
                    active
                      ? "bg-navy text-pearl border-navy shadow-lg"
                      : has
                      ? "bg-white text-navy border-navy/15 hover:border-gold hover:-translate-y-0.5"
                      : "bg-white/40 text-navy/30 border-navy/5 cursor-not-allowed"
                  }`}
                >
                  <span className="font-display text-lg leading-none">{m}</span>
                  <span className={`mt-1.5 w-1.5 h-1.5 rounded-full ${active ? "bg-gold" : has ? "bg-gold/80" : "bg-transparent"}`} />
                </button>
              );
            })}
          </div>
          {activeMonth != null && (
            <button
              onClick={() => setActiveMonth(null)}
              data-testid="clear-month"
              className="mt-4 text-xs uppercase tracking-widest text-saffron hover:text-saffron-light"
            >
              Clear month filter ✕
            </button>
          )}
        </div>
      </section>

      {/* EVENTS LIST */}
      <section className="py-20 bg-pearl">
        <div className="section-container">
          {loading ? (
            <p className="text-slate-500">Loading events…</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <p className="font-display text-3xl text-navy">No events match your filter.</p>
              <button
                onClick={() => { setActiveMonth(null); setFilter("all"); }}
                className="mt-4 text-saffron hover:text-saffron-light text-sm underline underline-offset-2"
              >
                Reset filters
              </button>
            </div>
          ) : (
            <AnimatePresence mode="popLayout">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((e, i) => (
                  <motion.div
                    key={e.event_id}
                    layout
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.4) }}
                  >
                    <EventCard
                      e={e}
                      reserved={myRsvps.has(e.event_id)}
                      onRsvp={rsvp}
                      past={new Date(e.event_date).getTime() < now}
                    />
                  </motion.div>
                ))}
              </div>
            </AnimatePresence>
          )}
        </div>
      </section>

      {!user && (
        <section className="py-16 bg-navy text-pearl text-center">
          <div className="section-container">
            <p className="eyebrow text-gold">Stay in the loop</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl">RSVP and never miss a gathering.</h2>
            <Link to="/membership" className="inline-block mt-6 px-7 py-3 bg-gold text-navy hover:bg-gold-light rounded-sm" data-testid="events-cta-join">
              Become a member
            </Link>
          </div>
        </section>
      )}
    </>
  );
}

function EventCard({ e, reserved, onRsvp, past }) {
  const badge = modeBadge(e.mode);
  const Icon = badge.icon;
  const d = new Date(e.event_date);
  return (
    <article
      className={`bg-white border border-navy/10 rounded-sm overflow-hidden transition-all duration-500 flex flex-col h-full ${past ? "opacity-95" : "hover:-translate-y-1 hover:shadow-xl hover:border-gold/40"}`}
      data-testid={`event-card-${e.event_id}`}
    >
      <div className="aspect-[16/10] relative">
        <EventCover title={e.title} />
        {past && (
          <span className="absolute top-3 left-3 bg-navy text-pearl text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-sm">
            Past
          </span>
        )}
        <div className="absolute top-3 right-3 bg-pearl/95 text-navy rounded-sm shadow-md text-center min-w-14 px-2 py-1.5">
          <div className="text-[10px] uppercase tracking-widest text-saffron leading-none">{d.toLocaleDateString("en-GB", { month: "short" })}</div>
          <div className="font-display text-2xl leading-tight">{d.getDate()}</div>
        </div>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-2.5 py-1 rounded-sm ${badge.color}`}>
            <Icon size={12} /> {badge.label}
          </span>
          <EventTypeIcon title={e.title} size={16} className="text-gold" />
        </div>
        <h3 className="mt-4 font-display text-2xl text-navy leading-tight">{e.title}</h3>
        <p className="mt-2 text-sm text-slate-600 line-clamp-3 flex-1">{e.description}</p>
        <div className="mt-5 space-y-1.5 text-sm text-slate-700">
          <div className="flex items-center gap-2"><Calendar size={14} className="text-gold shrink-0" />
            {d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long", year: "numeric" })}
          </div>
          <div className="flex items-center gap-2"><Clock size={14} className="text-gold shrink-0" />
            {d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
          </div>
          <div className="flex items-center gap-2"><MapPin size={14} className="text-gold shrink-0" />{e.location}</div>
          {e.capacity && !past && (
            <div className="flex items-center gap-2"><UsersIcon size={14} className="text-gold shrink-0" />{e.rsvp_count || 0} / {e.capacity} attending</div>
          )}
        </div>
        {!past && (
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <button
              onClick={() => onRsvp(e.event_id)}
              disabled={reserved}
              data-testid={`rsvp-btn-${e.event_id}`}
              className={`flex-1 min-w-[120px] py-2.5 rounded-sm text-sm font-medium transition-colors ${
                reserved ? "bg-navy/10 text-navy" : "bg-navy text-pearl hover:bg-navy-light"
              }`}
            >
              {reserved ? (<span className="inline-flex items-center justify-center gap-1.5"><Check size={16} /> RSVP confirmed</span>) : "RSVP"}
            </button>
            <AddToCalendarButton
              data-testid={`add-cal-${e.event_id}`}
              event={{
                title: e.title,
                description: e.description || "",
                location: e.location || "",
                start: d,
                end: e.event_end ? new Date(e.event_end) : null,
                url: typeof window !== "undefined" ? `${window.location.origin}/events` : "",
              }}
            />
          </div>
        )}
      </div>
    </article>
  );
}
